import React, { useEffect, useState } from "react";
import DashboardBanner from "../../components/DashboardBanner";
import empty from "../../assets/images/empty.png";
import add from "../../assets/icons/Add.svg";
import deleteIcon from "../../assets/icons/delete.svg";
import AppBtn from "../../components/AppBtn";
import Modal from "../../components/modal";
import action from "../../assets/icons/menu.svg";
import AppInput from "../../components/AppInput";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { convertFromRaw, EditorState, convertToRaw } from "draft-js";
import api from "../../api/api";
import { journalsFromApiResponse } from "../../utils/journalsFromApiResponse";
import { toast } from "react-toastify";

export default function Journals() {
  const [journals, setJournals] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    volume: "",
    issue: "",
    doi: "",
    page: "",
    date: "",
    file: null,
  });

  useEffect(() => {
    fetchJournal();
    const handleOutsideClick = () => setOpenMenuId(null);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const fetchJournal = () => {
    setIsLoading(true);
    api
      .get("/api/journals/")
      .then((res) => {
        if (res?.status === 200) {
          setJournals(journalsFromApiResponse(res.data));
        }
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load journals";
        toast.error(message);
      })
      .finally(() => setIsLoading(false));
  };

  const handleEditorChange = (state) => setEditorState(state);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] ?? null;
    if (selectedFile && selectedFile.size === 0) {
      toast.warning("This file appears to be 0 KB on your device.");
    }
    setForm((prev) => ({ ...prev, file: selectedFile }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openEdit = (data) => {
    const initial = { ...data };
    initial.date = initial.date ? initial.date.split("T")[0] : "";
    setForm(initial);
    setIsEdit(true);
    setIsModalOpen(true);
    setIsView(false);
    setOpenMenuId(null);

    if (data.content) {
      try {
        const contentState = convertFromRaw(JSON.parse(data.content));
        setEditorState(EditorState.createWithContent(contentState));
      } catch (err) {
        console.error("Content parse error:", err);
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  };

  const openView = (data) => {
    setForm(data);
    setIsView(true);
    setOpenMenuId(null);

    if (data.content) {
      try {
        const contentState = convertFromRaw(JSON.parse(data.content));
        setEditorState(EditorState.createWithContent(contentState));
      } catch (err) {
        console.error("Content parse error:", err);
        setEditorState(EditorState.createEmpty());
      }
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const content = JSON.stringify(rawContent);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("volume", form.volume);
    formData.append("issue", form.issue);
    formData.append("doi", form.doi);
    formData.append("page", form.page);
    formData.append("date", form.date);
    formData.append("content", content);

    // Append file only if it's a new File object (not an existing URL string)
    if (form.file && typeof form.file !== "string") {
      formData.append("file", form.file);
    }

    try {
      if (!isEdit) {
        const res = await api.post("/api/journals/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.status === 201) {
          toast.success("Journal published successfully!");
          closeModal();
          fetchJournal();
        }
      } else {
        const journalId = form._id || form.id;
        const res = await api.put(`/api/journals/${journalId}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.status === 200) {
          toast.success("Journal updated successfully!");
          closeModal();
          fetchJournal();
        }
      }
    } catch (error) {
      const fileError = Array.isArray(error?.response?.data?.file)
        ? error.response.data.file[0]
        : null;
      const message =
        fileError ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "An error occurred";
      toast.error(message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteJournal = () => {
    api
      .delete(`/api/journals/${deleteId}/`)
      .then((res) => {
        if (res.status === 200) {
          toast.success("Journal deleted successfully");
          setIsDeleteModalOpen(false);
          fetchJournal();
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Delete failed");
      });
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
    setOpenMenuId(null);
  };

  const toggleMenu = (e, itemId) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === itemId ? null : itemId));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsView(false);
    setIsEdit(false);
    setForm({
      title: "",
      volume: "",
      issue: "",
      doi: "",
      page: "",
      date: "",
      file: null,
    });
    setEditorState(EditorState.createEmpty());
  };

  return (
    <div className="flex flex-col gap-6">
      <DashboardBanner
        title="All journal"
        description="Welcome! Easily post, edit, and delete journals to keep track of your journal history"
        routeTo="Go to dashboard"
        route="/"
      />

      <div className="flex justify-between items-center">
        <span className="text-lg">All Journals</span>
        <AppBtn onClick={() => setIsModalOpen(true)} variant="primary">
          <img src={add} alt="add" />
          Add Article
        </AppBtn>
      </div>

      {/* Table Section */}
      <div className="flex bg-white big_card">
        {journals.length > 0 && !isLoading && (
          <div className="flex flex-col w-full">
            {/* Header */}
            <div className="grid grid-cols-5 md:grid-cols-7 gap-4 bg-blue py-3 px-4 rounded-lg w-full border-b border-b-primary">
              <div className="w-full col-span-4 md:col-span-3">Title</div>
              <div className="w-full hidden md:flex md:col-span-2">Date</div>
              <div className="w-full hidden md:block">Volume</div>
              <div></div>
            </div>

            {/* Rows */}
            {journals.map((journal, index) => {
              const journalId = journal._id || journal.id;
              return (
                <div key={journalId}>
                  <div className="grid grid-cols-5 md:grid-cols-7 gap-4 py-4 items-center px-4 rounded-lg hover:bg-partial-white cursor-pointer">
                    <div className="pr-4 w-full truncate capitalize flex col-span-4 md:col-span-3">
                      <span className="truncate w-[98%]">{journal.title}</span>
                    </div>
                    <div className="pr-4 w-full truncate hidden md:flex md:col-span-2">
                      {new Date(journal.date).toLocaleString("en-US", {
                        month: "long",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                    <div className="md:flex gap-2 items-center hidden">
                      <span className="truncate capitalize">{journal.volume}</span>
                    </div>

                    {/* Action Menu */}
                    <div className="flex justify-end relative">
                      <img
                        onClick={(e) => toggleMenu(e, journalId)}
                        width={20}
                        height={20}
                        className="cursor-pointer"
                        src={action}
                        alt="action"
                      />

                      {openMenuId === journalId && (
                        <div
                          className="absolute top-8 right-0 w-[160px] bg-white shadow-lg rounded-md py-1 z-50 border border-gray-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            onClick={() => openView(journal)}
                            className="px-6 py-2 text-deep-blue hover:bg-gray-100 cursor-pointer"
                          >
                            View
                          </div>
                          <div
                            onClick={() => openEdit(journal)}
                            className="px-6 py-2 text-deep-blue hover:bg-gray-100 cursor-pointer"
                          >
                            Edit
                          </div>
                          <div
                            onClick={() => openDeleteModal(journalId)}
                            className="px-6 py-2 text-red-600 hover:bg-gray-100 cursor-pointer"
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {index !== journals.length - 1 && (
                    <div className="w-full h-[1px] bg-[#e5e5e5]"></div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {journals.length === 0 && !isLoading && (
          <div className="min-h-[200px] py-8 w-full flex flex-col gap-4 justify-center items-center text-light-blue text-xl">
            <img height="200" width="200" src={empty} alt="" />
            No journal
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center items-center w-full h-[200px]">
            <span className="main_loader"></span>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal isOpen={isModalOpen}>
        <form
          onSubmit={publish}
          className="w-full lg:w-[900px] px-4 py-6 bg-white h-full lg:h-[80vh] overflow-y-scroll flex flex-col gap-4 lg:rounded-lg"
        >
          <h2 className="text-xl font-medium text-primary">
            {isEdit ? "Edit Journal" : "Add New Journal"}
          </h2>

          <AppInput
            label="Title"
            required
            type="text"
            onChange={handleChange}
            value={form.title}
            name="title"
            placeholder="Enter journal title"
          />
          <AppInput
            label="Volume"
            required
            type="text"
            onChange={handleChange}
            value={form.volume}
            name="volume"
            placeholder="Enter volume"
          />
          <AppInput
            label="Issue"
            required
            type="number"
            onChange={handleChange}
            value={form.issue}
            name="issue"
            placeholder="Enter issue"
          />

          {/* PDF File — shown on both create and edit */}
          <div className="flex flex-col gap-1">
            <AppInput
              label={isEdit ? "Replace PDF File (optional)" : "PDF File"}
              required={!isEdit}
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              name="file"
            />
            {/* Show current file link when editing */}
            {isEdit && form.file && typeof form.file === "string" && (
              <div className="text-sm text-gray-500 mt-1">
                Current file:{" "}
                <a
                  href={form.file}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 underline"
                >
                  View uploaded PDF
                </a>
                <span className="ml-2 text-gray-400">
                  — upload a new file above to replace it
                </span>
              </div>
            )}
            {form.file && typeof form.file !== "string" && (
              <div className="text-sm text-gray-500 mt-1">
                Selected file: {form.file.name} ({(form.file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>

          <AppInput
            label="DOI"
            required
            type="text"
            onChange={handleChange}
            value={form.doi}
            name="doi"
            placeholder="Enter DOI"
          />
          <AppInput
            label="Page"
            required
            type="text"
            onChange={handleChange}
            value={form.page}
            name="page"
            placeholder="Enter page"
          />
          <AppInput
            label="Date"
            required
            type="date"
            onChange={handleChange}
            value={form.date}
            name="date"
          />

          <Editor
            wrapperClassName="editor-wrapper"
            editorClassName="editor"
            editorState={editorState}
            onEditorStateChange={handleEditorChange}
            toolbar={{
              options: ["inline", "list", "textAlign", "history", "fontSize", "fontFamily"],
              inline: { inDropdown: true, options: ["bold", "italic", "underline"] },
              list: { inDropdown: true, options: ["unordered", "ordered"] },
              textAlign: { options: ["left", "center", "right", "justify"] },
              fontSize: { options: [8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30] },
              fontFamily: {
                options: ["Arial", "Georgia", "Impact", "Tahoma", "Times New Roman", "Verdana"],
              },
            }}
          />

          <div className="w-full mt-10 flex gap-3 justify-end">
            <AppBtn type="button" onClick={closeModal} variant="danger">
              Close
            </AppBtn>
            <AppBtn isLoading={isLoading} type="submit" variant="primary">
              {isEdit ? "Update Article" : "Publish Article"}
            </AppBtn>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen}>
        <div className="rounded-xl w-[98%] lg:w-[600px] bg-white py-5 px-4 flex items-center text-dark-grey text-center flex-col gap-6">
          <div className="w-[80px] h-[80px] bg-red-200 rounded-full flex items-center justify-center">
            <img height="40" width="40" src={deleteIcon} alt="delete" />
          </div>
          Are you sure you want to delete this journal? <br /> This action cannot be undone.
          <div className="flex justify-end items-center gap-4">
            <AppBtn onClick={() => setIsDeleteModalOpen(false)} variant="outline">
              Cancel
            </AppBtn>
            <AppBtn onClick={deleteJournal} variant="danger">
              Delete
            </AppBtn>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isView}>
        <div className="lg:h-[60vh] overflow-y-scroll h-full w-full lg:w-[800px] lg:rounded-lg bg-white px-4 py-6 flex flex-col gap-6">
          <h2 className="text-xl font-medium text-primary">Journal Details</h2>

          <div className="flex flex-col gap-1">
            <h2 className="text-primary">Title</h2>
            <span className="capitalize">{form.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <h2 className="text-primary">Volume</h2>
              <span>{form.volume}</span>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-primary">Issue</h2>
              <span>{form.issue}</span>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-primary">DOI</h2>
              <span>{form.doi}</span>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-primary">Date</h2>
              <span>
                {form.date
                  ? new Date(form.date).toLocaleString("en-US", {
                      month: "long",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>

          {/* PDF link in view modal */}
          {form.file && typeof form.file === "string" && (
            <div className="flex flex-col gap-1">
              <h2 className="text-primary">PDF File</h2>
              <a
                href={form.file}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 underline text-sm"
              >
                View / Download PDF
              </a>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <h2 className="text-primary">Content</h2>
            <Editor toolbarHidden editorState={editorState} readOnly />
          </div>

          <div className="flex justify-end gap-4">
            <AppBtn variant="outline" onClick={() => openEdit(form)}>
              Edit
            </AppBtn>
            <AppBtn variant="primary" onClick={closeModal}>
              Close
            </AppBtn>
          </div>
        </div>
      </Modal>
    </div>
  );
}